// Nhãn tiếng Việt, câu cố định và bảng câu lỗi của trang Studio (SPEC Studio mục 4.10, 6.6, 7.5, 8.7, 8.10).
// Chữ hiển thị: Creator (C hoa), Nhân viên, Studio, Nhóm nhảy, Quản lý, Nhóm trưởng; "kim cương" trong câu,
// "KC" trong tiêu đề cột / ô số. Nhãn hành động của backend: approval-labels.ts (ACTION_LABEL) — bản sao ở đây
// chỉ để dựng bộ lọc; bảng nhật ký hiện `actionLabel` do máy chủ trả.

import type {
  ApprovalKind,
  FlagSeverity,
  ImpactUnit,
  LogMethod,
  ManagerInvalidReason,
  StaffTier,
  StudioLinkRelation,
  StudioMemberBase,
  StudioQueueKind,
  StudioRequestKind,
  StudioRequestStatus,
  StudioScopeMode,
  StudioTeamKind,
  StudioTeamMemberRole,
  StudioViewerKind,
} from './contract';

type Option<T extends string> = { value: T; label: string };

function toOptions<T extends string>(map: Record<T, string>): Option<T>[] {
  return (Object.keys(map) as T[]).map((value) => ({ value, label: map[value] }));
}

export const labelOf = (map: Record<string, string>, value?: string | null, fallback = '—') =>
  value ? map[value] ?? value : fallback;

// ------------------------------------------------------------------ hằng ---

/** Chép từ BE/studios/studio.constants.ts: loại việc tab "Cần xử lý" có nút để làm (mục 7.5). */
export const STUDIO_TAB_KINDS: readonly ApprovalKind[] = ['LEAVE', 'CLAIM'];
/** Q7 và POST /approvals/bulk: tối đa 20 dòng mỗi lần. */
export const STUDIO_BULK_MAX = 20;
/** S24: đề nghị nhập KC chỉ cho ngày trong [hôm nay UTC − 35, hôm nay UTC]. */
export const STUDIO_KC_REQUEST_WINDOW_DAYS = 35;
/** S24: trần số KC của một đề nghị. */
export const STUDIO_KC_REQUEST_MAX = 2_000_000_000;
/** Lý do bắt buộc: sau trim 5–500 ký tự (assertReason). */
export const REASON_MIN = 5;
export const REASON_MAX = 500;

// ------------------------------------------------------------- người xem ---

export const TIER_LABEL: Record<Exclude<StaffTier, null>, string> = {
  ADMIN: 'Admin',
  SMOD: 'SMOD',
  OPS_MANAGER: 'Quản lý',
};
export const TIER_COLOR: Record<Exclude<StaffTier, null>, string> = {
  ADMIN: 'magenta',
  SMOD: 'purple',
  OPS_MANAGER: 'purple',
};

export const VIEWER_KIND_LABEL: Record<StudioViewerKind, string> = {
  ADMIN: 'Admin',
  SMOD: 'SMOD',
  MANAGER: 'Quản lý Studio',
  OPS_NO_STUDIO: 'Quản lý chưa có Studio',
  NONE: '—',
};

// --------------------------------------------------------------- Studio ---

/** Tooltip của thẻ "Chưa hợp lệ" cạnh tên Quản lý (mục 8.7.1). */
export const MANAGER_INVALID_TEXT: Record<Exclude<ManagerInvalidReason, null>, string> = {
  NOT_OPS_MANAGER:
    'Người này không phải tài khoản MOD hoặc Agency nên không đứng tên trưởng team được. Hãy đổi Quản lý.',
  DISABLED: 'Tài khoản Quản lý đang bị khoá. Hãy đổi Quản lý.',
  MULTI_STUDIO:
    'Người này đang là Quản lý của nhiều Studio. Hãy đổi Quản lý để mỗi người chỉ quản lý một Studio.',
};
export const MANAGER_INVALID_TAG = 'Chưa hợp lệ';
export const NO_MANAGER_TEXT = 'Chưa có Quản lý';

/** Cảnh báo đầu bảng danh sách Studio khi có Studio mà Quản lý không phải Quản lý vận hành (mục 8.7.1). */
export const managerInvalidWarning = (count: number) =>
  `${count} Studio có Quản lý chưa hợp lệ. Với các Studio này phạm vi Studio không có tác dụng: người đứng tên Quản lý thao tác theo quyền chung. Lưu ý: đổi phòng ban của một Quản lý mà vẫn giữ vai trò MOD sẽ đưa họ vào nhóm quyền rộng hơn (SMOD), không thu hẹp quyền.`;

/** Modal Đổi Quản lý và modal Xoá Studio: câu cảnh báo theo STUDIO_SCOPE_MODE (mục 8.7.1). KHÔNG khuyên đổi phòng ban. */
export const MANAGER_CHANGE_WARNING: Record<StudioScopeMode, string> = {
  strict:
    'Quản lý cũ sẽ không còn duyệt được việc của bất kỳ Creator nào cho tới khi được giao Studio mới.',
  owners:
    'Lưu ý: sau khi thôi quản lý Studio này, Quản lý cũ trở lại quyền của Quản lý vận hành chung và vẫn duyệt được việc của Creator toàn công ty, kể cả Studio này. Nếu không muốn vậy, hãy hạ vai trò MOD của tài khoản đó, hoặc đề nghị chuyển hệ thống sang chế độ phạm vi chặt (STUDIO_SCOPE_MODE=strict). Không đổi phòng ban để thu quyền: MOD ở phòng khác có quyền duyệt rộng hơn.',
  off: 'Phạm vi Studio đang tắt: việc đổi Quản lý chưa làm thay đổi quyền duyệt của ai.',
};

/** Dòng chú thích đầu tab Tổng quan cho Quản lý Studio (mục 8.7.4); chế độ khác không hiện. */
export const OVERVIEW_SCOPE_NOTE: Partial<Record<StudioScopeMode, string>> = {
  off: 'Phạm vi Studio chưa bật: bạn đang duyệt theo quyền Quản lý vận hành chung.',
};

export const DELETE_STUDIO_TEXT =
  'Xoá Studio sẽ gỡ toàn bộ thành viên, lưu trữ các Nhóm nhảy và huỷ đề nghị đang chờ. Dữ liệu kim cương từ TikTok không bị xoá.';
export const TIKTOK_GROUP_HINT =
  'Tên này quyết định Creator nào trong file TikTok tự vào Studio. Khác với nhóm "HP STUDIO" của TikTok.';

export const STUDIO_SORT_LABEL: Record<'kc_desc' | 'title' | 'created_desc', string> = {
  kc_desc: 'KC tháng này (cao → thấp)',
  title: 'Tên Studio (A → Z)',
  created_desc: 'Mới tạo trước',
};
export const STUDIO_SORT_OPTIONS = toOptions(STUDIO_SORT_LABEL);

// ------------------------------------------------------------ thành viên ---

export const MEMBER_TYPE_LABEL: Record<StudioMemberBase['memberType'], string> = {
  CREATOR: 'Creator',
  STAFF: 'Nhân viên',
  ASSISTANT: 'Trợ lý',
  MANAGER: 'Quản lý',
};
export const MEMBER_TYPE_COLOR: Record<StudioMemberBase['memberType'], string> = {
  CREATOR: 'magenta',
  STAFF: 'blue',
  ASSISTANT: 'purple',
  MANAGER: 'gold',
};
/** Bộ lọc "Loại" của S10 (không có MANAGER: Quản lý không nằm trong danh sách). */
export const MEMBER_TYPE_FILTER_OPTIONS: Option<'CREATOR' | 'STAFF' | 'ASSISTANT'>[] = [
  { value: 'CREATOR', label: 'Creator' },
  { value: 'STAFF', label: 'Nhân viên' },
  { value: 'ASSISTANT', label: 'Trợ lý' },
];

export const MEMBER_SOURCE_LABEL: Record<StudioMemberBase['source'], string> = {
  FILE: 'Từ file',
  MOVED: 'Đã chuyển',
  MANUAL: 'Thêm tay',
  CHANNEL: 'Kênh',
};
export const MEMBER_SOURCE_OPTIONS = toOptions(MEMBER_SOURCE_LABEL);

export const MEMBER_SORT_LABEL: Record<'kc_desc' | 'name' | 'live_days_desc', string> = {
  kc_desc: 'KC tháng (cao → thấp)',
  name: 'Tên (A → Z)',
  live_days_desc: 'Ngày LIVE (nhiều → ít)',
};
export const MEMBER_SORT_OPTIONS = toOptions(MEMBER_SORT_LABEL);

export const INACTIVE_TAG = 'Không còn trong file TikTok';
export const UID_ONLY_TAG = 'Chỉ có UID';
export const NO_ACCOUNT_TAG = 'Chưa có tài khoản';
export const PENDING_ADMIN_TAG = 'Đang chờ Admin';

/** Dòng tóm tắt đầu tab Thành viên (mục 8.7.5). */
export const membersSummaryLine = (s: {
  members: number;
  withAccount: number;
  uidOnly: number;
  inactive: number;
  unassignedToTeam: number;
}) =>
  `${s.members} thành viên · ${s.withAccount} có tài khoản app · ${s.uidOnly} chỉ có UID · ${s.inactive} không còn trong file TikTok · ${s.unassignedToTeam} chưa xếp Nhóm nhảy`;

/** Tooltip cột Creator của danh sách Studio (mục 8.7.1). */
export const creatorCountTooltip = (c: { withAccount: number; uidOnly: number; inactive: number }) =>
  `${c.withAccount} có tài khoản app · ${c.uidOnly} chỉ có UID · ${c.inactive} không còn trong file TikTok`;

export const familyMismatchWarning = (count: number) =>
  `${count} tài khoản được ghi ở Studio này nhưng UID TikTok của họ thuộc nhóm file khác nên không tính là thành viên.`;

// ------------------------------------------------------------ Nhóm nhảy ---

export const TEAM_KIND_LABEL: Record<StudioTeamKind, string> = {
  DANCE: 'Nhóm nhảy',
  NPC: 'Nhóm NPC',
  LIVE_CHANNEL: 'Kênh LIVE',
};
export const TEAM_ROLE_LABEL: Record<StudioTeamMemberRole, string> = {
  LEADER: 'Nhóm trưởng',
  DANCER: 'Thành viên',
  SUPPORT: 'Hỗ trợ',
};
/** S19 chỉ nhận DANCER / SUPPORT (Nhóm trưởng đặt qua S16). */
export const TEAM_ADD_ROLE_OPTIONS: Option<'DANCER' | 'SUPPORT'>[] = [
  { value: 'DANCER', label: 'Thành viên' },
  { value: 'SUPPORT', label: 'Hỗ trợ' },
];
export const KC_MODE_LABEL: Record<'CHANNEL' | 'MEMBERS', string> = {
  CHANNEL: 'KC theo kênh',
  MEMBERS: 'KC theo thành viên',
};
export const CHANNEL_OUTSIDE_TEXT = 'Kênh không còn thuộc Studio';
export const CHANNEL_OUTSIDE_ADMIN_HINT = 'Gắn lại kênh hoặc bỏ kênh.';
export const CHANNEL_NOT_MEMBER_HINT = 'Chưa thuộc Studio. Gửi đề nghị thêm người trước.';
export const NO_KC_THIS_MONTH = 'Tháng này chưa có số';
export const unassignedLine = (n: number) => `${n} thành viên chưa xếp nhóm`;
export const moveTeamConfirm = (from: string, to: string) => `Chuyển từ ${from} sang ${to}?`;

// -------------------------------------------------------------- đề nghị ---

export const REQUEST_KIND_LABEL: Record<StudioRequestKind, string> = {
  MEMBER_ADD: 'Thêm người vào Studio',
  MEMBER_REMOVE: 'Gỡ người khỏi Studio',
  KC_MANUAL: 'Nhập kim cương',
  TEAM_CHANNEL: 'Gắn kênh Nhóm nhảy',
};
export const REQUEST_KIND_OPTIONS = toOptions(REQUEST_KIND_LABEL);

/** Nhãn bộ lọc "loại việc" (mục 7.2, 8.7.2). Không chọn = cả hai loại. */
export const QUEUE_KIND_LABEL: Record<StudioQueueKind, string> = {
  STUDIO_MEMBER: 'Thêm / gỡ người, gắn kênh',
  STUDIO_KC: 'Nhập kim cương',
};
export const QUEUE_KIND_OPTIONS = toOptions(QUEUE_KIND_LABEL);

export const REQUEST_STATUS_LABEL: Record<StudioRequestStatus, string> = {
  PENDING: 'Chờ Admin',
  APPROVED: 'Đã duyệt',
  REJECTED: 'Từ chối',
  CANCELLED: 'Đã huỷ',
};
export const UNAPPLIED_LABEL = 'Đã duyệt – chưa ghi được số';
export const WRITTEN_NO_LOG_LABEL = 'Đã duyệt – số đã ghi, thiếu nhật ký';
/** Thẻ đỏ trên dòng hàng đợi khi written = true (mục 7.2). */
export const WRITTEN_NO_LOG_TAG = 'Số đã ghi, thiếu nhật ký';
export const WRITTEN_NO_LOG_TOOLTIP =
  'Kim cương đã vào hệ thống nhưng chưa có dòng nhật ký. Bấm "Thử ghi lại" để hoàn tất.';

/** Nhãn trạng thái đề nghị theo mục 6.6 (tính cả applied / written). */
export function requestStatusLabel(row: {
  status?: StudioRequestStatus;
  applied: boolean;
  written: boolean;
}): string {
  const status = row.status ?? 'PENDING';
  if (status === 'APPROVED' && !row.applied) {
    return row.written ? WRITTEN_NO_LOG_LABEL : UNAPPLIED_LABEL;
  }
  return REQUEST_STATUS_LABEL[status] ?? status;
}

export function requestStatusColor(row: {
  status?: StudioRequestStatus;
  applied: boolean;
  written: boolean;
}): string {
  const status = row.status ?? 'PENDING';
  if (status === 'APPROVED') return row.applied ? 'green' : 'red';
  if (status === 'REJECTED') return 'volcano';
  if (status === 'CANCELLED') return 'default';
  return 'gold';
}

/**
 * Bộ lọc trạng thái của trang Đề nghị (mục 8.7.2). QUEUE = "Chờ Admin" đọc hàng đợi Q2 (gồm cả đề nghị
 * "Đã duyệt – chưa ghi được số"); các giá trị khác là `status` của Q3 / S25.
 */
export type RequestStatusFilter = 'QUEUE' | StudioRequestStatus | 'UNAPPLIED' | 'ALL';
export const REQUEST_STATUS_FILTER_OPTIONS: Option<RequestStatusFilter>[] = [
  { value: 'QUEUE', label: 'Chờ Admin' },
  { value: 'UNAPPLIED', label: UNAPPLIED_LABEL },
  { value: 'APPROVED', label: 'Đã duyệt' },
  { value: 'REJECTED', label: 'Từ chối' },
  { value: 'CANCELLED', label: 'Đã huỷ' },
  { value: 'ALL', label: 'Tất cả' },
];

export const REQUEST_SENT_NOTE =
  'Đề nghị được gửi tới Admin. Hệ thống chỉ thực hiện sau khi Admin duyệt.';
export const BULK_MIXED_TOOLTIP = 'Chọn các đề nghị cùng loại để duyệt một lượt';
export const KC_DAY_HAS_FILE_HINT = 'Ngày này đã có số từ file TikTok';
export const pendingRequestsLine = (n: number) => `Đề nghị đang chờ Admin: ${n}`;
export const pendingRequestsLineSmod = (n: number) => `Đề nghị chờ Admin: ${n}`;

// --------------------------------------------------- hàng đợi đơn thành viên ---

export const APPROVAL_KIND_LABEL: Record<ApprovalKind, string> = {
  OT: 'Tăng ca',
  LEAVE: 'Nghỉ phép',
  SHOP: 'Đơn Shop Sao',
  WAIVE: 'Xin xoá phạt',
  CLAIM: 'Nhiệm vụ Sao',
};
/** Danh từ trong câu ("Duyệt đơn nghỉ phép", "Từ chối báo cáo nhiệm vụ"). */
export const APPROVAL_KIND_NOUN: Record<ApprovalKind, string> = {
  OT: 'đơn tăng ca',
  LEAVE: 'đơn nghỉ phép',
  SHOP: 'đơn quà',
  WAIVE: 'đơn xin xoá phạt',
  CLAIM: 'báo cáo nhiệm vụ',
};
export const IMPACT_UNIT_LABEL: Record<ImpactUnit, string> = {
  MIN: 'phút',
  DAY: 'ngày',
  STAR: 'Sao',
  ITEM: 'món',
  KC: 'KC',
};
export const LEAVE_REVIEW_OFF_TEXT =
  'Duyệt nghỉ phép đang tắt cho Quản lý. Admin bật ở trang Quyền hạn.';
export const kindOffText = (kind: ApprovalKind) =>
  kind === 'LEAVE' ? LEAVE_REVIEW_OFF_TEXT : `${APPROVAL_KIND_LABEL[kind]} đang tắt cho Quản lý.`;
export const OT_ON_APP_NOTE = 'Duyệt tăng ca trên app';

// ------------------------------------------------------ nhật ký, cờ ---

/** Nhãn hành động (approval-labels.ts ACTION_LABEL + mục 6.6). Dùng cho bộ lọc "Việc". */
export const ACTION_LABEL: Record<string, string> = {
  STUDIO_CREATE: 'Tạo Studio',
  STUDIO_EDIT: 'Sửa Studio',
  STUDIO_MANAGER_CHANGE: 'Đổi Quản lý Studio',
  STUDIO_DELETE: 'Xoá Studio',
  STUDIO_MEMBER_ADD: 'Thêm vào Studio',
  STUDIO_MEMBER_REMOVE: 'Gỡ khỏi Studio',
  STUDIO_MEMBER_MOVE: 'Chuyển Studio',
  TEAM_CREATE: 'Tạo Nhóm nhảy',
  TEAM_EDIT: 'Sửa Nhóm nhảy',
  TEAM_CHANNEL_SET: 'Gắn / bỏ kênh Nhóm nhảy',
  TEAM_ARCHIVE: 'Lưu trữ Nhóm nhảy',
  TEAM_RESTORE: 'Mở lại Nhóm nhảy',
  TEAM_MEMBER_ADD: 'Xếp vào Nhóm nhảy',
  TEAM_MEMBER_REMOVE: 'Rời Nhóm nhảy',
  TEAM_MEMBER_MOVE: 'Chuyển Nhóm nhảy',
  STUDIO_REQUEST_CREATE: 'Gửi đề nghị',
  STUDIO_REQUEST_CANCEL: 'Huỷ đề nghị',
  STUDIO_REQUEST_APPROVE: 'Duyệt đề nghị',
  STUDIO_REQUEST_REJECT: 'Từ chối đề nghị',
  KC_MANUAL: 'Nhập KC tay',
  LEAVE_APPROVE: 'Duyệt nghỉ phép',
  LEAVE_REJECT: 'Từ chối nghỉ phép',
  CLAIM_APPROVE: 'Duyệt nhiệm vụ',
  CLAIM_REJECT: 'Từ chối nhiệm vụ',
  PENALTY_CREATE: 'Ghi phạt',
  PENALTY_CANCEL: 'Huỷ phạt',
  WAIVE_APPROVE: 'Duyệt xoá phạt',
  WAIVE_REJECT: 'Từ chối xoá phạt',
  OT_APPROVE: 'Duyệt tăng ca',
  OT_REJECT: 'Từ chối tăng ca',
  OT_EDIT: 'Sửa phiếu tăng ca',
  OT_REVOKE: 'Thu hồi tăng ca',
  ATT_CREATE: 'Tạo chấm công',
  ATT_EDIT: 'Sửa chấm công',
  ATT_APPROVE: 'Duyệt chấm công',
  SHOP_DELIVER: 'Giao quà',
  SHOP_REJECT: 'Từ chối đơn quà',
  ITEM_UPDATE: 'Sửa món Shop',
  STAR_ADJUST: 'Điều chỉnh Sao',
  ACCOUNT_PATCH: 'Sửa ví Sao',
  MISSION_EDIT: 'Tạo, sửa nhiệm vụ Sao',
  PK_CREATE: 'Tạo trận PK',
  PK_UPDATE: 'Sửa trận PK',
  PK_DELETE: 'Xoá trận PK',
  ROLE_CHANGE: 'Đổi quyền',
  DEPT_CHANGE: 'Đổi phòng ban',
  PERMISSION_CHANGE: 'Đổi quyền hạn',
  DENIED: 'Bị chặn tự xử lý',
  DISPUTE: 'Báo sai',
  FLAG_CLOSE: 'Khép cờ',
};
export const LOG_ACTION_OPTIONS = Object.keys(ACTION_LABEL).map((value) => ({
  value,
  label: ACTION_LABEL[value],
}));

export const METHOD_LABEL: Record<LogMethod, string> = {
  TAP: 'Bấm duyệt',
  BATCH: 'Hàng loạt',
  QR: 'Quét QR',
  CODE: 'Nhập mã',
  MANUAL: 'Giao tay',
  AUTO: 'Tự động',
};

export const LINK_RELATION_LABEL: Record<StudioLinkRelation, string> = {
  ENTITY: 'Về Studio',
  SUBJECT: 'Về thành viên',
  ACTOR: 'Do Quản lý làm',
};
export const LINK_RELATION_OPTIONS = toOptions(LINK_RELATION_LABEL);

/** Nhãn cờ (approval-labels.ts FLAG_LABEL + ba cờ Studio mục 6.2). Máy chủ đã trả `label`; bảng này là dự phòng. */
export const FLAG_LABEL: Record<string, string> = {
  STUDIO_SCOPE: 'Ngoài phạm vi Studio',
  MEMBER_MOVE_LATE_MONTH: 'Thêm người cuối tháng',
  STUDIO_KC_AFTER_CLOSE: 'Ghi số cho đề nghị đã đóng',
  OT_LONG: 'Dài > 3 giờ',
  NEAR_CAP: 'Gần trần OT',
  OT_OVER_CAP: 'Vượt trần OT',
  BACKDATED: 'Khai/sửa muộn',
  STATUS_UPGRADE: 'Trễ → đúng giờ',
  ATT_REPEAT: 'Sửa công nhiều lần',
  STAR_ADJUST_BIG: 'Sao lớn',
  KC_OVERRIDE: 'Ghi đè KC',
  OFF_HOURS: 'Ngoài giờ',
  MANUAL_DELIVERY: 'Giao không mã',
  MANUAL_RATIO: 'Hay giao không mã',
  ITEM_PRICE_DROP: 'Giảm giá mạnh',
  PK_AFTER_START: 'Sửa sau giờ đấu',
  PRIVILEGE_CHANGE: 'Đổi quyền',
  PERMISSION_CHANGE: 'Đổi quyền hạn',
  SELF_ADMIN: 'Admin tự duyệt',
  DENIED_REPEAT: 'Cố tự duyệt',
  DISPUTE: 'Bị báo sai',
  CROSS_APPROVAL: 'Duyệt chéo',
  STAR_ADJUST_PAIR: 'Cộng Sao qua lại',
  OT_EDITED_AFTER_APPROVE: 'Sửa OT sau duyệt',
  ADMIN_LOCKED_EDIT: 'Sửa tháng đã chốt',
  PAIR_CONCENTRATION: 'Cặp quen',
  BURST: 'Duyệt dồn dập',
  STAR_CONCENTRATION: 'Sao dồn một người',
  PENALTY_CONCENTRATION: 'Phạt dồn một người',
  OT_NO_ATTENDANCE: 'OT không có công',
  SEAL_MISMATCH: 'Niêm phong lệch',
  TRIGGER_MISSING: 'Thiếu trigger',
};
export const flagLabel = (code: string, serverLabel?: string | null) =>
  serverLabel || FLAG_LABEL[code] || code;
export const FLAG_CODE_OPTIONS = Object.keys(FLAG_LABEL).map((value) => ({
  value,
  label: FLAG_LABEL[value],
}));

export const FLAG_SEVERITY_LABEL: Record<FlagSeverity, string> = {
  RED: 'Cờ đỏ',
  SECOND: 'Cần Admin xem lại',
  YELLOW: 'Cờ vàng',
};
export const FLAG_SEVERITY_COLOR: Record<FlagSeverity, string> = {
  RED: 'red',
  SECOND: 'purple',
  YELLOW: 'gold',
};
export const FLAG_SEVERITY_OPTIONS = toOptions(FLAG_SEVERITY_LABEL);

export const FLAG_STATUS_LABEL: Record<'OPEN' | 'OK' | 'ACTION', string> = {
  OPEN: 'Đang mở',
  OK: 'Hợp lệ',
  ACTION: 'Cần xử lý',
};
export const FLAG_STATUS_OPTIONS = toOptions(FLAG_STATUS_LABEL);
export const FLAG_SCOPE_NOTE = 'Cờ tổng hợp theo kỳ tính theo thành viên hiện tại';

/** Tên trường trong `changes[]` của dòng nhật ký (khoá before/after của mục 6.1). */
export const FIELD_LABEL: Record<string, string> = {
  title: 'Tên Studio',
  description: 'Mô tả',
  avatar: 'Ảnh đại diện',
  tiktokGroupName: 'Nhóm trong file TikTok',
  managerId: 'Mã Quản lý',
  managerName: 'Quản lý',
  assistantIds: 'Trợ lý',
  memberCount: 'Số thành viên',
  teamCount: 'Số Nhóm nhảy',
  memberKeys: 'Thành viên',
  deleted: 'Đã xoá',
  cancelledRequests: 'Đề nghị bị huỷ',
  studioId: 'Mã Studio',
  studioTitle: 'Studio',
  key: 'Khoá thành viên',
  uid: 'UID TikTok',
  name: 'Tên',
  via: 'Cách thêm',
  requestId: 'Mã đề nghị',
  teamId: 'Mã Nhóm nhảy',
  teamName: 'Nhóm nhảy',
  role: 'Vai trò',
  kind: 'Loại',
  leaderUserId: 'Nhóm trưởng',
  note: 'Ghi chú',
  channelTiktokUid: 'UID kênh',
  channelName: 'Kênh',
  pinned: 'Ghim thành viên',
  status: 'Trạng thái',
  auto: 'Tự động',
  requestKind: 'Loại đề nghị',
  payload: 'Nội dung đề nghị',
  evidenceUrl: 'Minh chứng',
  applied: 'Đã ghi số',
  kc: 'Kim cương',
  kcDate: 'Ngày KC',
  requesterId: 'Người đề nghị',
  starsDelta: 'Sao thay đổi',
  applyWarning: 'Cảnh báo khi ghi',
  statusAtWrite: 'Trạng thái lúc ghi',
  scopeMode: 'Chế độ phạm vi',
  attempted: 'Thao tác bị chặn',
  code: 'Mã lỗi',
  group: 'Nhóm',
};
export const fieldLabel = (field: string) => FIELD_LABEL[field] ?? field;

// ------------------------------------------------------------- câu chung ---

export const STUDIO_UNAVAILABLE_TEXT = 'Chức năng Studio chưa sẵn sàng. Vui lòng báo quản trị.';
export const NETWORK_ERROR_TEXT = 'Không kết nối được máy chủ.';
export const GENERIC_ERROR_TEXT = 'Có lỗi xảy ra. Vui lòng thử lại.';
export const ESTIMATE_TAG = 'ước tính';
export const MANUAL_TAG = 'nhập tay';
export const NEW_TAG = 'mới';
export const NO_STUDIO_TITLE = 'Bạn chưa được giao Studio nào';
export const NO_STUDIO_TEXT = 'Liên hệ Admin để được giao Studio.';
export const OUT_OF_SCOPE_TITLE = 'Trang này không thuộc phạm vi Quản lý Studio';
export const OUT_OF_SCOPE_TEXT = 'Bạn dùng được mục Studio của tôi, Phạt và Hộp thư hỗ trợ.';
export const KC_LATE_NOTE = 'File TikTok thường chậm 1 ngày.';
export const monthNoDataNote = (mm: string) => `Tháng ${mm} chưa có dữ liệu. ${KC_LATE_NOTE}`;

/** Câu khi bảng / tab rỗng (mục 8.7). */
export const EMPTY_TEXT = {
  studios: 'Chưa có Studio nào. Bấm "Tạo Studio" để bắt đầu.',
  requestsQueue: 'Không có đề nghị nào đang chờ.',
  requestsMine: 'Chưa có đề nghị nào.',
  overviewNoUid: 'Studio chưa có Creator nào có UID TikTok nên chưa có số kim cương.',
  members: 'Studio chưa có thành viên.',
  membersAdmin: 'Bấm "Thêm thành viên".',
  membersManager: 'Bấm "Đề nghị thêm người" để gửi Admin.',
  teams: 'Studio chưa có Nhóm nhảy.',
  kc: 'Chưa có dữ liệu kim cương cho các tháng đã chọn. File TikTok thường chậm 1 ngày.',
  queueMembers: 'Không có đơn nào đang chờ.',
  queueRequests: 'Chưa có đề nghị nào.',
  log: 'Chưa có thao tác nào được ghi cho Studio này.',
  monitorFlags: 'Không có cờ nào đang mở.',
  monitorLog: 'Chưa có thao tác nào.',
  chart: 'Chưa có dữ liệu',
} as const;

// ------------------------------------------------------------ câu lỗi ---

/**
 * Câu của mã lỗi (mapStudioError dùng khi máy chủ không gửi câu tiếng Việt ở `error`).
 * STUDIO_* và KC_DAY_HAS_FILE_DATA chép từ BE/studios/studio.errors.ts (STUDIO_POLICY_ERROR_TEXT +
 * STUDIO_ERROR_TEXT); APPROVAL_* từ BE/approvals/core/approval-errors.ts; STAR_* là các mã luồng đề nghị /
 * duyệt của Studio có thể gặp (mã STAR_* khác: studio-error.ts hỏi mapStarError).
 */
export const STUDIO_ERROR_TEXT: Record<string, string> = {
  STUDIO_SCOPE: 'Người này không thuộc Studio bạn quản lý.',
  STUDIO_NOT_MANAGER: 'Bạn chưa được giao Studio nào. Liên hệ Admin.',
  STUDIO_ADMIN_ONLY: 'Việc này chỉ Admin làm. Quản lý gửi đề nghị ở mục Cần xử lý.',
  STUDIO_KC_REQUEST_ONLY:
    'Quản lý Studio không nhập kim cương trực tiếp. Hãy gửi đề nghị để Admin duyệt.',
  STUDIO_STAR_ADJUST_DENIED: 'Quản lý Studio không điều chỉnh Sao. Việc này do Admin làm.',
  STUDIO_ACCOUNT_PATCH_DENIED: 'Quản lý Studio không sửa ví Sao.',
  STUDIO_MISSION_EDIT_DENIED:
    'Quản lý Studio không tạo hay sửa nhiệm vụ Sao. Việc này do Admin làm.',
  STUDIO_ROUTE_BLOCKED: 'Quản lý Studio không dùng được chức năng này. Việc này do Admin làm.',
  STUDIO_UNAVAILABLE: 'Chức năng Studio chưa sẵn sàng, vui lòng báo quản trị.',
  STUDIO_NOT_FOUND: 'Không tìm thấy Studio.',
  STUDIO_ROLE_NOT_ALLOWED: 'Tài khoản của bạn không dùng được chức năng Studio.',
  STUDIO_USE_STUDIO_API: 'Nhóm này là Studio. Hãy thao tác ở trang Quản trị > Studio.',
  STUDIO_LEAVE_NOT_ALLOWED: 'Không tự rời Studio được. Hãy báo Quản lý hoặc Admin.',
  STUDIO_TYPE_LOCKED: 'Không đổi được loại nhóm này.',
  STUDIO_INVALID_MONTH: 'Tháng không hợp lệ (YYYY-MM).',
  STUDIO_INVALID_DATE: 'Ngày không hợp lệ (YYYY-MM-DD).',
  STUDIO_TITLE_EXISTS: 'Tên Studio đã tồn tại.',
  STUDIO_TIKTOK_NAME_TAKEN: 'Tên nhóm trong file TikTok đã gắn với Studio khác.',
  STUDIO_USER_NOT_FOUND: 'Không tìm thấy tài khoản.',
  STUDIO_UID_NOT_FOUND: 'UID TikTok này chưa có trong dữ liệu đã nhập.',
  STUDIO_MANAGER_INVALID: 'Quản lý (trưởng team) phải là tài khoản MOD hoặc Agency đang hoạt động.',
  STUDIO_MANAGER_TAKEN: 'Người này đang quản lý Studio khác.',
  STUDIO_MANAGER_IS_MEMBER: 'Người này đang là thành viên của Studio khác.',
  STUDIO_MANAGER_SAME: 'Người này đang là Quản lý của Studio.',
  STUDIO_MEMBER_INVALID: 'Không thêm được tài khoản có quyền duyệt hoặc đang quản lý Studio khác.',
  STUDIO_MEMBER_OWNER_ELSEWHERE: 'Hãy đổi Quản lý của Studio kia trước khi chuyển tài khoản này.',
  STUDIO_MEMBER_ALREADY: 'Người này đã thuộc Studio.',
  STUDIO_MEMBER_NOT_FOUND: 'Thành viên không còn trong Studio.',
  STUDIO_MEMBER_IS_MANAGER: 'Không gỡ được Quản lý. Hãy đổi Quản lý trước.',
  STUDIO_CANDIDATE_QUERY_SHORT: 'Nhập ít nhất 2 ký tự để tìm.',
  STUDIO_TEAM_NOT_FOUND: 'Không tìm thấy Nhóm nhảy.',
  STUDIO_TEAM_ARCHIVED: 'Nhóm nhảy đã lưu trữ.',
  STUDIO_TEAM_NAME_EXISTS: 'Studio đã có Nhóm nhảy tên này.',
  STUDIO_TEAM_LEADER_INVALID: 'Nhóm trưởng phải là thành viên có tài khoản của nhóm.',
  STUDIO_TEAM_MEMBER_NOT_IN_STUDIO: 'Người này không thuộc Studio.',
  STUDIO_TEAM_MEMBER_ALREADY: 'Người này đang ở một Nhóm nhảy khác.',
  STUDIO_TEAM_MEMBER_IS_CHANNEL: 'UID của người này đang là kênh của một Nhóm nhảy khác.',
  STUDIO_TEAM_MEMBER_NOT_FOUND: 'Người này không còn trong Nhóm nhảy.',
  STUDIO_CHANNEL_ADMIN_ONLY: 'UID kênh do Admin đặt. Quản lý gửi đề nghị gắn kênh.',
  STUDIO_CHANNEL_UID_TAKEN: 'UID kênh đã gắn với Nhóm nhảy khác.',
  STUDIO_CHANNEL_UID_IS_MEMBER: 'UID này đang là thành viên của một Nhóm nhảy khác.',
  STUDIO_CHANNEL_UID_OTHER_STUDIO: 'UID kênh đang thuộc Studio khác. Hãy chuyển Studio trước.',
  STUDIO_CHANNEL_UID_NOT_MEMBER:
    'UID này chưa thuộc Studio. Gửi đề nghị thêm người trước, rồi đề nghị gắn kênh.',
  STUDIO_REQUEST_MANAGER_ONLY: 'Chỉ Quản lý Studio gửi đề nghị. Admin thao tác trực tiếp.',
  STUDIO_REQUEST_NOT_FOUND: 'Không tìm thấy đề nghị.',
  STUDIO_REQUEST_NOT_PENDING: 'Đề nghị này đã được xử lý.',
  STUDIO_REQUEST_NOT_APPLICABLE: 'Đề nghị này không ở trạng thái chờ ghi số.',
  STUDIO_REQUEST_ALREADY_WRITTEN:
    'Số kim cương của đề nghị này đã được ghi vào hệ thống nên không từ chối được. Hãy bấm "Thử ghi lại" để hoàn tất nhật ký.',
  STUDIO_HAS_UNAPPLIED_REQUEST:
    'Studio còn đề nghị nhập kim cương đã duyệt nhưng chưa ghi xong. Hãy bấm "Thử ghi lại" hoặc từ chối các đề nghị đó rồi mới xoá Studio.',
  STUDIO_REQUEST_NOT_OWNER: 'Chỉ người gửi mới huỷ được đề nghị.',
  STUDIO_REQUEST_SELF: 'Không tự đề nghị hoặc tự duyệt cho chính mình.',
  STUDIO_REQUEST_DUPLICATE: 'Đã có đề nghị đang chờ cho người này.',
  STUDIO_REQUEST_TOO_MANY: 'Bạn đang có 20 đề nghị chưa xong. Chờ Admin xử lý bớt rồi gửi tiếp.',
  STUDIO_REQUEST_STALE: 'Đề nghị không còn phù hợp với dữ liệu hiện tại. Hãy từ chối và gửi lại.',
  STUDIO_REQUEST_KIND_INVALID: 'Loại đề nghị không hợp lệ.',
  STUDIO_EVIDENCE_INVALID: 'Minh chứng phải là ảnh tải lên từ trang này.',
  STUDIO_EVIDENCE_REQUIRED: 'Đề nghị nhập kim cương phải kèm ảnh chụp Backstage.',
  STUDIO_KC_DATE_RANGE:
    'Chỉ đề nghị nhập kim cương cho ngày trong 35 ngày gần đây, không sau hôm nay.',
  KC_DAY_HAS_FILE_DATA:
    'Ngày này đã có số từ file TikTok. Số từ file là số chuẩn; cần sửa thì Admin nhập lại file.',
  STUDIO_BULK_TOO_MANY: 'Mỗi lần duyệt tối đa 20 đề nghị.',
  STUDIO_KC_COMPARE_INVALID: 'Chọn 1 đến 3 tháng so sánh, khác tháng gốc.',
  // approval-errors.ts
  APPROVAL_SELF_ACTION: 'Không thể tự xử lý việc của chính mình.',
  APPROVAL_NEEDS_ADMIN: 'Việc này chỉ Admin xử lý.',
  APPROVAL_ROLE_NOT_ALLOWED: 'Vai trò của bạn không làm được việc này.',
  APPROVAL_SEGMENT_NOT_ALLOWED: 'Người này không thuộc nhóm bạn phụ trách.',
  APPROVAL_KIND_NOT_ALLOWED: 'Bạn không duyệt loại việc này.',
  APPROVAL_PAYROLL_LOCKED: 'Tháng này đã chốt phiếu lương, chỉ Admin sửa được.',
  APPROVAL_REASON_REQUIRED: 'Cần nhập lý do (ít nhất 5 ký tự).',
  APPROVAL_TAP_NOT_ALLOWED: 'Quản lý chỉ giao quà bằng QR hoặc mã nhận hàng.',
  APPROVAL_BULK_TOO_MANY: 'Mỗi lần duyệt tối đa 20 đơn.',
  APPROVAL_LOG_NOT_FOUND: 'Không tìm thấy dòng lịch sử.',
  APPROVAL_DISPUTE_NOT_SUBJECT: 'Chỉ người được nhắc tới mới báo sai được.',
  APPROVAL_DISPUTE_DUPLICATE: 'Bạn đã báo sai dòng này rồi.',
  APPROVAL_FLAG_CLOSED: 'Cờ này đã được khép.',
  APPROVAL_AUDIT_UNAVAILABLE: 'Chưa sẵn sàng ghi nhật ký, vui lòng báo quản trị.',
  APPROVAL_SUBJECT_NOT_FOUND: 'Không tìm thấy người được xử lý.',
  // Mã Điểm Sao mà luồng Studio gặp (manualKc khi Admin duyệt đề nghị nhập KC, duyệt nhiệm vụ).
  STAR_KC_SELF: 'Không thể nhập KC cho chính mình.',
  STAR_KC_NO_UID: 'Người này chưa có TikTok UID trong hồ sơ.',
  STAR_REVIEW_SELF: 'Không thể tự xử lý đơn/phạt/nhiệm vụ của chính mình.',
  STAR_REVIEW_NOT_PENDING: 'Báo cáo này đã được xử lý trước đó.',
  STAR_REVIEW_OWN_MISSION:
    'Nhiệm vụ này do bạn tạo hoặc sửa, nên bạn không duyệt báo cáo của nó — Admin hoặc quản lý khác duyệt.',
};
